import React from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import Skeleton from 'core/components/Skeleton';

export type SummaryCardTone = 'default' | 'info' | 'warning' | 'danger';

export type SummaryCardItem = {
  id: string;
  label: string;
  value: number | string;
  tone?: SummaryCardTone;
};

type Part = 'root' | 'item' | 'value' | 'label' | 'divider';

type Props = {
  items: SummaryCardItem[];
  selectedId?: string | null;
  onSelect?: ((id: string) => void) | null;
  isLoading?: boolean;
  className?: string;
  classes?: Partial<Record<Part, string>>;
  skeletonItemCount?: number;
};

const TONE_VALUE_CLASSES: Record<SummaryCardTone, string> = {
  default: 'text-gray-900',
  info: 'text-blue-600',
  warning: 'text-amber-600',
  danger: 'text-red-600',
};

const DEFAULT_SKELETON_ITEM_COUNT = 4;

type SummaryCardItemContentProps = {
  item: SummaryCardItem;
  isSelected: boolean;
  isClickable: boolean;
  classes?: Partial<Record<Part, string>>;
  showDivider: boolean;
  onSelect?: ((id: string) => void) | null;
};

type SummaryCardItemBodyProps = {
  item: SummaryCardItem;
  isSelected: boolean;
  classes?: Partial<Record<Part, string>>;
};

function SummaryCardItemBody({
  item,
  isSelected,
  classes,
}: SummaryCardItemBodyProps) {
  return (
    <>
      <div className="flex flex-col items-start gap-2">
        <span
          className={clsx(
            'text-xl font-bold leading-none',
            TONE_VALUE_CLASSES[item.tone ?? 'default'],
            classes?.value,
          )}
        >
          {item.value}
        </span>
        <span
          className={clsx(
            'text-xs font-semibold',
            isSelected ? 'text-blue-600' : 'text-gray-500',
            classes?.label,
          )}
        >
          {item.label}
        </span>
      </div>
      {isSelected ? (
        <span className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-blue-600" aria-hidden />
      ) : null}
    </>
  );
}

function SummaryCardItemContent({
  item,
  isSelected,
  isClickable,
  classes,
  showDivider,
  onSelect,
}: SummaryCardItemContentProps) {
  const itemClassName = clsx(
    'relative flex flex-1 flex-col items-start gap-0.5 border-0 px-4 py-3 text-left outline-none',
    isSelected ? 'bg-blue-50' : 'bg-transparent',
    {
      'border-r border-r-gray-200': showDivider,
      'cursor-pointer': isClickable,
      [classes?.divider ?? '']: showDivider,
    },
    classes?.item,
  );

  return isClickable ? (
    <button
      type="button"
      className={itemClassName}
      onClick={() => onSelect?.(item.id)}
    >
      <SummaryCardItemBody
        item={item}
        isSelected={isSelected}
        classes={classes}
      />
    </button>
  ) : (
    <div className={itemClassName}>
      <SummaryCardItemBody
        item={item}
        isSelected={isSelected}
        classes={classes}
      />
    </div>
  );
}

type SummaryCardSkeletonProps = {
  count: number;
  classes?: Partial<Record<Part, string>>;
};

function SummaryCardSkeleton({ count, classes }: SummaryCardSkeletonProps) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => `skeleton-${index}`).map((key, index) => (
        <div
          key={key}
          className={clsx(
            'flex flex-1 flex-col items-start gap-2 px-4 py-3',
            {
              'border-r border-r-gray-200': index < count - 1,
              [classes?.divider ?? '']: index < count - 1,
            },
            classes?.item,
          )}
        >
          <Skeleton variant="rectangular" className={clsx('!h-5 !w-8 !rounded', classes?.value)} />
          <Skeleton variant="rectangular" className={clsx('!h-3 !w-16 !rounded', classes?.label)} />
        </div>
      ))}
    </>
  );
}

export default function SummaryCard({
  items,
  selectedId,
  onSelect,
  isLoading = false,
  className,
  classes,
  skeletonItemCount = DEFAULT_SKELETON_ITEM_COUNT,
}: Props) {
  const isClickable = !isNil(onSelect);

  return (
    <div
      className={clsx(
        'flex w-full overflow-hidden rounded-lg border border-gray-200 bg-white',
        classes?.root,
        className,
      )}
    >
      {isLoading ? (
        <SummaryCardSkeleton count={skeletonItemCount} classes={classes} />
      ) : (
        items.map((item, index) => (
          <SummaryCardItemContent
            key={item.id}
            item={item}
            isSelected={selectedId === item.id}
            isClickable={isClickable}
            classes={classes}
            showDivider={index < items.length - 1}
            onSelect={onSelect}
          />
        ))
      )}
    </div>
  );
}
